import { Component, OnInit, Input } from '@angular/core';
import { DataService } from '../../services/data.service';
import * as d3 from 'd3';
import { constants } from 'projects/marketpro-ui/src/app/constants';
import { SizeOfPrize } from '../../models/statistics.model';
import { AuthService } from '../../../auth/services/auth.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'mpro-piechart',
  templateUrl: './piechart.component.html',
  styleUrls: ['./piechart.component.scss'],
})
export class Piechart implements OnInit {
  public legendItems = constants.legendItems;
  @Input() public countries: SizeOfPrize[] = [];
  @Input() public year: number;
  @Input() public isFreemium: boolean;
  public pieChartData: any = [];
  private maxLabelLength = 10;
  private authService: AuthService;
  private ngUnsubscribe: Subject<void> = new Subject();
  constructor(public dataService: DataService) {}

  public ngOnInit() {
    this.pieChartData = [];
  }

  public ngOnDestroy() {
    this.pieChartData = [];
  }

  public destroyChart() {
    d3.select('#piechart').selectAll('svg').remove();
  }

  groupByContinentName(
    array: Array<{ continentName: string; [key: string]: any }>
  ) {
    const result = array.reduce((acc, curr) => {
      const { continentName } = curr;
      if (!acc[continentName]) {
        const continentColor = constants.legendItems.find(
          (continentColor) => continentColor.text === continentName
        )?.color;
        acc[continentName] = {
          name: continentName,
          color: continentColor,
          year: this.year,
          people: 0,
          value: 0,
          children: [],
        };
      }
      acc[continentName].children.push({
        continentName: curr.continentName,
        name: curr.countryName,
        value: curr.totalAmount,
        size: curr.totalAmount,
        people: curr.population,
        year: this.year,
        color: constants.legendItems.find(
          (continentColor) => continentColor.text === curr.continentName
        )?.color,
      });
      return acc;
    }, {} as { [key: string]: { name: string; year: number; people: number; value: number; color: string | undefined; children: Array<{ continentName: string; [key: string]: any }> } });

    Object.values(result).forEach((continent) => {
      continent.people = continent.children.reduce(
        (sum, child) => sum + child.people,
        0
      );
      continent.value = continent.children.reduce(
        (sum, child) => sum + child.size,
        0
      );
    });
    return Object.values(result);
  }

  ngOnChanges() {
    this.destroyChart();
    this.pieChartData = [];
    if (this.isFreemium) {
      const formattedData = this.countries.map((continent) => ({
        ...continent,
        name: continent.continentName,
        value: continent.totalAmount,
        size: continent.totalAmount,
        color: constants.legendItems.find(
          (continentColor) => continentColor.text === continent.continentName
        )?.color,
        people: continent.population,
      }));

      const pieChartData: any = {
        name: '',
        color: '#fff',
        children: formattedData.map((continent) => ({
          name: continent.name,
          year: continent.year,
          size: continent.value,
          value: continent.value,
          color: continent.color,
          people: continent.people,
        })),
      };
      this.pieChartData = pieChartData;
    } else {
      const pieChartData = {
        name: '',
        color: '#fff',
        children: this.groupByContinentName(this.countries),
      };
      this.pieChartData = pieChartData;
    }
    this.drawChart();
  }

  protected drawChart() {
    const piechartContainer: HTMLDivElement | null =
      document.querySelector('#piechart')!;

    const svg: any = d3
      .select(piechartContainer)
      .append('svg')
      .attr('width', piechartContainer.clientWidth)
      .attr('height', 456)
      .attr('viewBox', [
        -piechartContainer.clientWidth / 2,
        -456 / 2,
        piechartContainer.clientWidth,
        456,
      ] as any)
      .attr('style', 'max-width: 100%; height: auto; font: 10px sans-serif;');

    const g = svg.append('g');

    g.classed('sunburst-chart-inner-container', true).attr(
      'transform',
      `translate(${5}, ${5})`
    );

    const root = d3.hierarchy(this.pieChartData);

    // Apply zoom and drag

    function zoomed({ transform }) {
      g.attr('transform', transform);
    }
    const zoom = d3.zoom().scaleExtent([1, 40]).on('zoom', zoomed);
    svg.call(zoom);

    setTimeout(() => {
      d3.select('#zoom-in').on('click', function () {
        svg.transition().call(zoom.scaleBy, 2);
      });

      d3.select('#zoom-out').on('click', function () {
        svg.transition().call(zoom.scaleBy, 0.5);
      });
    }, 500);

    const valueAccessor = (d) => d.size;
    // compute the values of internal nodes by aggregating from the leaves.
    valueAccessor == null
      ? root.count()
      : root.sum((d) => Math.max(0, valueAccessor(d)));

    root.sort((a, b) => d3.descending(a.value, b.value));

    const radius =
      Math.min(piechartContainer.clientWidth, piechartContainer.clientHeight) /
      2;

    d3.partition().size([2 * Math.PI, radius])(root);

    const arc: any = d3
      .arc()
      .startAngle((d: any) => d.x0)
      .endAngle((d: any) => d.x1)
      .padAngle((d: any) => Math.min((d.x1 - d.x0) / 2, (2 * 5) / radius))
      .padRadius(radius / 2)
      .innerRadius((d: any) => d.y0)
      .outerRadius((d: any) => d.y1 - 5);

    const cell = g.selectAll('a').data(root.descendants()).join('a');

    cell
      .append('path')
      .attr('d', arc)
      .attr('fill', ({ data }) => data.color)
      .on('mouseover', function (event, d) {
        if (!d.data.name) {
          return;
        }
        d3.select(this).attr('stroke', 'black');
        const tooltip = d3.select('.tooltip');
        tooltip.style('opacity', 1);
        tooltip
          .html(
            `
              <h3>${d.data.name} in ${d.data.year}</h3>
              <div style="display: flex; justify-content: space-between">
                <div>
                  <strong>${d.data.people?.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}</strong>
                  <p>Population</p>
                </div>
                <div style="text-align: right">
                  <strong>
                  $${d.data.value?.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                  </strong>
                  <p>Total spending in USD</p>
                </div>
              </div>
           `
          )
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke', 'none');
        d3.select('.tooltip').style('opacity', 0);
      })
      .on('mousemove', function (event) {
        d3.select('.tooltip')
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .attr('fill-opacity', 1);

    // label is simply the given name
    const labelAccessor = (d) => d.name;
    const label = labelAccessor;

    // add labels
    if (label != null)
      cell
        // filter cells without room for label
        .filter((d: any) => ((d.y0 + d.y1) / 2) * (d.x1 - d.x0) > 10)
        .append('text')
        .classed('sunburst-node-label', true)
        .text((d) => {
          return d && d.data.name.length > this.maxLabelLength
            ? `${d.data.name.slice(0, this.maxLabelLength - 3)}...`
            : d.data.name;
        })
        // rotate and position labels
        .attr('transform', (d: any) => {
          // except base node
          // if (!d.depth) return;
          const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
          const y = (d.y0 + d.y1 + (x > 180 ? 45 : -45)) / 2;
          return `rotate(${x - 90}) translate(${y},0) rotate(${
            x < 180 ? 0 : 180
          })`;
        })
        .attr('dy', '0.32em');
  }
}
